import { useEffect, useState, useCallback } from "react";
import { Calendar, RefreshCw, ChevronRight, ArrowDownToLine, AlertCircle, TrendingUp, TrendingDown, Activity, Users } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import "../admin-affiliate-premium.css";

const fmtBDT = (n) => `৳ ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtBD = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { timeZone: "Asia/Dhaka", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) + " BD" : "—";

function StatTile({ label, value, sub, tone = "gold", icon: Icon }) {
  const tones = {
    gold: { c: "#f4e9a1", b: "rgba(212,175,55,0.45)", g: "rgba(212,175,55,0.16)" },
    emerald: { c: "#6ee7b7", b: "rgba(16,185,129,0.45)", g: "rgba(16,185,129,0.16)" },
    rose: { c: "#fda5b1", b: "rgba(229,75,89,0.45)", g: "rgba(229,75,89,0.16)" },
    blue: { c: "#93c5fd", b: "rgba(59,130,246,0.45)", g: "rgba(59,130,246,0.16)" },
    slate: { c: "#cbd5e1", b: "rgba(148,163,184,0.32)", g: "rgba(148,163,184,0.08)" },
  };
  const t = tones[tone] || tones.gold;
  return (
    <div className="ap-glass" style={{ padding: 16, background: `linear-gradient(135deg, ${t.g}, transparent 70%)`, border: `1px solid ${t.b}` }}>
      <div className="flex items-start justify-between mb-1">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/55">{label}</div>
        {Icon && <Icon size={14} style={{ color: t.c }} />}
      </div>
      <div className="text-white font-black tracking-tight" style={{ fontSize: 22, color: t.c }}>{value}</div>
      {sub && <div className="text-[11px] text-white/45 mt-1">{sub}</div>}
    </div>
  );
}

export default function AffiliateCalendarCycles() {
  const [current, setCurrent] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settling, setSettling] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [cycleDetail, setCycleDetail] = useState(null);
  const [selectedAff, setSelectedAff] = useState(null);
  const [playerBreakdown, setPlayerBreakdown] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, lRes] = await Promise.all([
        api.get("/admin/safe-cycle/current"),
        api.get("/admin/safe-cycle/list?limit=24"),
      ]);
      setCurrent(cRes.data);
      setList(lRes.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load cycles");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const settleNow = async () => {
    if (!window.confirm("Manually run settlement for the most recently ended cycle?\nThis is idempotent — won't create duplicates.")) return;
    setSettling(true);
    try {
      const { data } = await api.post("/admin/aff-commissions/run-calendar-safe-credit");
      toast.success(`Settlement done — Created ${data.created}, Credited ${fmtBDT(data.credited_total)}, Deficits ${data.deficits_carried}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Settlement failed");
    } finally { setSettling(false); }
  };

  const openCycle = async (label) => {
    setSelectedCycle(label);
    setCycleDetail(null);
    setSelectedAff(null);
    setPlayerBreakdown(null);
    try {
      const { data } = await api.get(`/admin/safe-cycle/${encodeURIComponent(label)}/affiliates`);
      setCycleDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load cycle");
    }
  };

  const openAffiliate = async (cycleLabel, accountId, username) => {
    setSelectedAff({ accountId, username });
    setPlayerBreakdown(null);
    try {
      const { data } = await api.get(`/admin/safe-cycle/${encodeURIComponent(cycleLabel)}/affiliate/${accountId}/players`);
      setPlayerBreakdown(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load player breakdown");
    }
  };

  if (loading) return <div className="text-white/60 p-6" data-testid="cycles-loading">Loading cycles…</div>;

  const cyc = current?.cycle || {};
  const totals = current?.totals || {};
  const cfg = current?.config || {};

  return (
    <div className="space-y-5" data-testid="page-calendar-cycles">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Calendar size={18} style={{ color: "#d4af37" }} />
            <h1 className="text-[20px] font-black text-white">Calendar Cycles · Safe Commission</h1>
          </div>
          <div className="text-[12px] text-white/55">
            Cycle A: 1–15 → settles 16th @ 00:05 BD &nbsp;·&nbsp; Cycle B: 16–end → settles 1st @ 00:05 BD next month
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="ap-btn-ghost" data-testid="btn-refresh-cycles">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={settleNow} disabled={settling} className="ap-btn-gold" data-testid="btn-settle-now">
            <ArrowDownToLine size={14} /> {settling ? "Settling…" : "Settle Now"}
          </button>
        </div>
      </div>

      {/* Current cycle overview */}
      <div className="ap-glass" style={{ padding: 20, border: "1px solid rgba(34,192,115,0.42)", background: "radial-gradient(700px 220px at 0% 0%, rgba(34,192,115,0.16), transparent 65%), linear-gradient(180deg, rgba(15,30,22,0.7), rgba(8,14,22,0.95))" }} data-testid="current-cycle-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <div className="flex items-center gap-2">
              <Activity size={14} style={{ color: "#22c073" }} />
              <span className="text-white font-bold">Current Open Cycle</span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: "rgba(212,175,55,0.18)", color: "#d4af37", border: "1px solid rgba(212,175,55,0.45)" }}>LIVE</span>
            </div>
            <div className="text-[20px] font-black text-white mt-1">Cycle {cyc.cycle_type} · {cyc.year}-{String(cyc.month).padStart(2, "0")}</div>
            <div className="text-[11.5px] text-white/55 mt-0.5">
              {fmtDate(cyc.start_iso)} → {fmtDate(cyc.end_iso)} · Settles: <span style={{ color: "#d4af37" }}>{fmtBD(cyc.settle_iso)}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-bold">Projected Payout (this cycle)</div>
            <div className="text-[26px] font-black mt-0.5" style={{ color: totals.projected_payout >= 0 ? "#22c073" : "#ef4444" }} data-testid="projected-payout">
              {fmtBDT(totals.projected_payout)}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <StatTile label="Active Affiliates" value={totals.active_affiliates ?? 0} icon={Users} tone="gold" />
          <StatTile label="Real Deposits" value={fmtBDT(totals.total_real_deposits)} icon={TrendingUp} tone="emerald" />
          <StatTile label="Withdrawals" value={fmtBDT(totals.total_withdrawals)} icon={TrendingDown} tone="rose" />
          <StatTile label="Bonus/Rebate" value={fmtBDT(totals.total_rebates)} tone="amber" />
          <StatTile label="Net Diff" value={fmtBDT(totals.total_net_diff)} tone={totals.total_net_diff >= 0 ? "emerald" : "rose"} />
          <StatTile label="Carry Deficit" value={fmtBDT(totals.total_previous_deficit)} tone="slate" />
        </div>
        <div className="mt-3 text-[11px] text-white/45">
          Commission Rate: <span style={{ color: "#d4af37" }}>{cfg.commission_pct}%</span> · Gateway Fee: {cfg.gateway_fee_pct}% · Wagering: {cfg.wagering_ratio}× · Min Payout: {fmtBDT(cfg.min_payout)}
        </div>
      </div>

      {/* Active affiliates current-cycle table */}
      {(current?.rows || []).length > 0 && (
        <div className="ap-glass" style={{ padding: 16 }}>
          <div className="text-white font-bold mb-3 flex items-center gap-2">
            <Users size={14} style={{ color: "#d4af37" }} /> Current Cycle — Affiliate Breakdown (Live)
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="current-cycle-table">
              <thead>
                <tr className="text-white/60">
                  <th className="text-left py-2 px-2">Affiliate</th>
                  <th className="text-right py-2 px-2">Players</th>
                  <th className="text-right py-2 px-2">Deposits</th>
                  <th className="text-right py-2 px-2">Withdrawals</th>
                  <th className="text-right py-2 px-2">Net Diff</th>
                  <th className="text-right py-2 px-2">Commission</th>
                  <th className="text-right py-2 px-2">Deficit</th>
                  <th className="text-right py-2 px-2">Projected</th>
                </tr>
              </thead>
              <tbody>
                {current.rows.map((r) => (
                  <tr key={r.account_id} className="border-t border-white/5">
                    <td className="py-2 px-2 text-white">{r.username} <span className="text-white/40 text-[10px]">({r.commission_pct}%)</span></td>
                    <td className="py-2 px-2 text-right text-white/80">{r.contributing_players}</td>
                    <td className="py-2 px-2 text-right text-white/80">{fmtBDT(r.real_deposits)}</td>
                    <td className="py-2 px-2 text-right text-white/80">{fmtBDT(r.withdrawals)}</td>
                    <td className="py-2 px-2 text-right" style={{ color: r.net_diff >= 0 ? "#22c073" : "#ef4444" }}>{fmtBDT(r.net_diff)}</td>
                    <td className="py-2 px-2 text-right" style={{ color: r.commission_signed >= 0 ? "#22c073" : "#ef4444" }}>{fmtBDT(r.commission_signed)}</td>
                    <td className="py-2 px-2 text-right text-white/60">{fmtBDT(r.previous_deficit)}</td>
                    <td className="py-2 px-2 text-right font-bold" style={{ color: r.projected_credit >= 0 ? "#22c073" : "#ef4444" }}>{fmtBDT(r.projected_credit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Historical cycles list */}
      <div className="ap-glass" style={{ padding: 16 }}>
        <div className="text-white font-bold mb-3 flex items-center gap-2">
          <Calendar size={14} style={{ color: "#d4af37" }} /> Settlement History (Last 24 Cycles)
        </div>
        {list.length === 0 ? (
          <div className="text-white/50 text-[12px] py-4 text-center" data-testid="no-history">
            <AlertCircle size={14} className="inline mr-1" /> No settled cycles yet. Run the &ldquo;Settle Now&rdquo; button or wait until the 16th / 1st.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="history-cycles-table">
              <thead>
                <tr className="text-white/60">
                  <th className="text-left py-2 px-2">Cycle</th>
                  <th className="text-left py-2 px-2">Period</th>
                  <th className="text-right py-2 px-2">Affiliates</th>
                  <th className="text-right py-2 px-2">Real Deposits</th>
                  <th className="text-right py-2 px-2">Net Loss</th>
                  <th className="text-right py-2 px-2">Commission Paid</th>
                  <th className="text-right py-2 px-2">Credited / Deficit</th>
                  <th className="text-right py-2 px-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.cycle_label} className="border-t border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => openCycle(r.cycle_label)}>
                    <td className="py-2 px-2 font-bold text-white">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] mr-1.5 ${r.cycle_type === "A" ? "bg-emerald-900/40 text-emerald-300" : "bg-blue-900/40 text-blue-300"}`}>{r.cycle_type}</span>
                      {r.cycle_label}
                    </td>
                    <td className="py-2 px-2 text-white/70">{fmtDate(r.period_start)} → {fmtDate(r.period_end)}</td>
                    <td className="py-2 px-2 text-right text-white/80">{r.affiliates_count}</td>
                    <td className="py-2 px-2 text-right text-white/80">{fmtBDT(r.total_real_deposits)}</td>
                    <td className="py-2 px-2 text-right text-white/80">{fmtBDT(r.total_net_loss)}</td>
                    <td className="py-2 px-2 text-right font-bold" style={{ color: "#22c073" }}>{fmtBDT(r.total_commission_paid)}</td>
                    <td className="py-2 px-2 text-right text-white/60">{r.credited_count} / <span style={{ color: "#ef4444" }}>{r.deficit_count}</span></td>
                    <td className="py-2 px-2 text-right">
                      <button className="text-[#d4af37] hover:text-[#f4e9a1] inline-flex items-center gap-1 text-[11px] font-bold" data-testid={`view-cycle-${r.cycle_label}`}>
                        View <ChevronRight size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cycle drill-down modal */}
      {selectedCycle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }} onClick={() => { setSelectedCycle(null); setSelectedAff(null); }} data-testid="cycle-detail-modal">
          <div className="bg-[#0a1018] border border-[#d4af37]/40 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#0a1018]/95 backdrop-blur p-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="text-white font-bold text-[16px]">Cycle {cycleDetail?.cycle_type} · {selectedCycle}</div>
                <div className="text-[11.5px] text-white/55 mt-0.5">{fmtDate(cycleDetail?.period_start)} → {fmtDate(cycleDetail?.period_end)}</div>
              </div>
              <button className="text-white/60 hover:text-white text-xl" onClick={() => { setSelectedCycle(null); setSelectedAff(null); }} data-testid="close-cycle-modal">✕</button>
            </div>
            <div className="p-5">
              {!cycleDetail ? (
                <div className="text-white/50 text-center py-6">Loading…</div>
              ) : selectedAff && playerBreakdown ? (
                <>
                  <button onClick={() => { setSelectedAff(null); setPlayerBreakdown(null); }} className="text-[#d4af37] text-[11px] mb-3 inline-flex items-center gap-1">← Back to affiliates</button>
                  <div className="text-white font-bold mb-2">{selectedAff.username} — Player-by-Player Safe Formula</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11.5px]" data-testid="player-breakdown-table">
                      <thead>
                        <tr className="text-white/60">
                          <th className="text-left py-2 px-2">Player</th>
                          <th className="text-right py-2 px-2">Real Dep</th>
                          <th className="text-right py-2 px-2">GW Fee</th>
                          <th className="text-right py-2 px-2">Withdrew</th>
                          <th className="text-right py-2 px-2">Rebate</th>
                          <th className="text-right py-2 px-2">Balance</th>
                          <th className="text-right py-2 px-2">Turnover</th>
                          <th className="text-center py-2 px-2">Wage</th>
                          <th className="text-right py-2 px-2">Net Diff</th>
                          <th className="text-right py-2 px-2">Net Loss</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(playerBreakdown.rows || []).map((p, i) => (
                          <tr key={i} className="border-t border-white/5">
                            <td className="py-1.5 px-2 text-white">{p.username || p.user_id?.slice(-6)}</td>
                            <td className="py-1.5 px-2 text-right text-white/80">{fmtBDT(p.real_deposits)}</td>
                            <td className="py-1.5 px-2 text-right text-white/60">{fmtBDT(p.gateway_fee)}</td>
                            <td className="py-1.5 px-2 text-right text-white/80">{fmtBDT(p.withdrawals_completed)}</td>
                            <td className="py-1.5 px-2 text-right text-white/60">{fmtBDT(p.rebates_paid)}</td>
                            <td className="py-1.5 px-2 text-right text-white/60">{fmtBDT(p.closing_balance)}</td>
                            <td className="py-1.5 px-2 text-right text-white/60">{fmtBDT(p.turnover)}</td>
                            <td className="py-1.5 px-2 text-center">{p.wagering_ok ? <span style={{ color: "#22c073" }}>✓</span> : <span style={{ color: "#ef4444" }}>✗</span>}</td>
                            <td className="py-1.5 px-2 text-right" style={{ color: p.net_diff >= 0 ? "#22c073" : "#ef4444" }}>{fmtBDT(p.net_diff)}</td>
                            <td className="py-1.5 px-2 text-right font-bold" style={{ color: "#d4af37" }}>{fmtBDT(p.net_loss)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {playerBreakdown.summary?.commission_amount > 0 && (
                    <div className="mt-3 p-3 rounded-lg" style={{ background: "rgba(34,192,115,0.10)", border: "1px solid rgba(34,192,115,0.35)" }}>
                      <span className="text-white/70 text-[11.5px]">Final Commission Credited: </span>
                      <span className="font-black text-[16px]" style={{ color: "#22c073" }}>{fmtBDT(playerBreakdown.summary.commission_amount)}</span>
                      <span className="text-white/50 text-[11px] ml-2">({playerBreakdown.summary.commission_rate}% × ৳{Number(playerBreakdown.summary.net_loss_for_commission || 0).toLocaleString()})</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]" data-testid="cycle-affiliates-table">
                    <thead>
                      <tr className="text-white/60">
                        <th className="text-left py-2 px-2">Affiliate</th>
                        <th className="text-right py-2 px-2">Players</th>
                        <th className="text-right py-2 px-2">Deposits</th>
                        <th className="text-right py-2 px-2">Net Loss</th>
                        <th className="text-right py-2 px-2">Rate</th>
                        <th className="text-right py-2 px-2">Credited</th>
                        <th className="text-right py-2 px-2">Deficit</th>
                        <th className="text-right py-2 px-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(cycleDetail.rows || []).map((r) => (
                        <tr key={r.id} className="border-t border-white/5">
                          <td className="py-2 px-2 text-white">{r.affiliate_username || r.promo_code}</td>
                          <td className="py-2 px-2 text-right text-white/80">{r.total_players}</td>
                          <td className="py-2 px-2 text-right text-white/80">{fmtBDT(r.total_deposits)}</td>
                          <td className="py-2 px-2 text-right text-white/80">{fmtBDT(r.net_loss_for_commission)}</td>
                          <td className="py-2 px-2 text-right text-white/60">{r.commission_rate}%</td>
                          <td className="py-2 px-2 text-right font-bold" style={{ color: "#22c073" }}>{fmtBDT(r.commission_amount)}</td>
                          <td className="py-2 px-2 text-right" style={{ color: r.new_deficit > 0 ? "#ef4444" : "#cbd5e1" }}>{fmtBDT(r.new_deficit)}</td>
                          <td className="py-2 px-2 text-right">
                            <button onClick={() => openAffiliate(selectedCycle, r.account_id, r.affiliate_username)} className="text-[#d4af37] hover:text-[#f4e9a1] text-[11px] font-bold" data-testid={`view-players-${r.account_id}`}>Players →</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
